import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatRupiah } from "../lib/api";
import {
  LayoutDashboard, Package, Briefcase, Calendar, MessageSquare, BookOpen,
  Tag, Users, Settings as Cog, ShoppingCart, ArrowLeft, LogOut, Brain, Crown, Ticket,
  Megaphone, Images, Bell, Mail
} from "lucide-react";
import AdminCrud from "./admin/AdminCrud";
import AdminUsers from "./admin/AdminUsers";
import AdminOrders from "./admin/AdminOrders";
import AdminSettings from "./admin/AdminSettings";
import AdminTests from "./admin/AdminTests";
import AdminLicenses from "./admin/AdminLicenses";
import AdminNewsletter from "./admin/AdminNewsletter";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Produk Digital", icon: Package },
  { to: "/admin/services", label: "Layanan", icon: Briefcase },
  { to: "/admin/counselors", label: "Counselors", icon: Users },
  { to: "/admin/events", label: "Event", icon: Calendar },
  { to: "/admin/tests", label: "Tes Psikologi", icon: Brain },
  { to: "/admin/licenses", label: "Lisensi Korporat", icon: Ticket },
  { to: "/admin/subscriptions", label: "Membership", icon: Crown },
  { to: "/admin/testimonials", label: "Testimoni", icon: MessageSquare },
  { to: "/admin/case-studies", label: "Studi Kasus", icon: BookOpen },
  { to: "/admin/vouchers", label: "Voucher", icon: Tag },
  { to: "/admin/orders", label: "Pesanan", icon: ShoppingCart },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/banners", label: "Hero Banner", icon: Images },
  { to: "/admin/popups", label: "Popup Banner", icon: Megaphone },
  { to: "/admin/announcements", label: "Announcement", icon: Bell },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/settings", label: "Pengaturan", icon: Cog },
];

const FIELDS = {
  products: [
    { name: "title", label: "Judul" },
    { name: "slug", label: "Slug" },
    { name: "category", label: "Kategori", type: "select", options: ["journaling", "mood_tracker", "hr_template", "other"] },
    { name: "description", label: "Deskripsi singkat", type: "textarea" },
    { name: "long_description", label: "Deskripsi panjang", type: "textarea" },
    { name: "price", label: "Harga", type: "number" },
    { name: "original_price", label: "Harga coret", type: "number" },
    { name: "image", label: "URL Gambar" },
    { name: "file_url", label: "URL File Digital" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
    { name: "featured", label: "Featured", type: "checkbox" },
  ],
  services: [
    { name: "title", label: "Judul" },
    { name: "slug", label: "Slug" },
    { name: "type", label: "Tipe", type: "select", options: ["counseling", "workshop", "training", "seminar"] },
    { name: "description", label: "Deskripsi singkat", type: "textarea" },
    { name: "long_description", label: "Deskripsi panjang", type: "textarea" },
    { name: "price", label: "Harga", type: "number" },
    { name: "duration_minutes", label: "Durasi (menit)", type: "number" },
    { name: "image", label: "URL Gambar" },
    { name: "counselor_id", label: "Counselor ID (opsional)" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  counselors: [
    { name: "name", label: "Nama" },
    { name: "title", label: "Gelar" },
    { name: "specialization", label: "Spesialisasi" },
    { name: "bio", label: "Bio", type: "textarea" },
    { name: "image", label: "URL Foto" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  events: [
    { name: "title", label: "Judul" },
    { name: "slug", label: "Slug" },
    { name: "description", label: "Deskripsi", type: "textarea" },
    { name: "long_description", label: "Deskripsi panjang", type: "textarea" },
    { name: "date", label: "Tanggal (ISO)" },
    { name: "location", label: "Lokasi" },
    { name: "price", label: "Harga", type: "number" },
    { name: "capacity", label: "Kapasitas", type: "number" },
    { name: "image", label: "URL Gambar" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  testimonials: [
    { name: "name", label: "Nama" },
    { name: "role", label: "Role" },
    { name: "content", label: "Isi", type: "textarea" },
    { name: "rating", label: "Rating (1-5)", type: "number" },
    { name: "image", label: "URL Foto" },
    { name: "is_featured", label: "Featured", type: "checkbox" },
  ],
  "case-studies": [
    { name: "title", label: "Judul" },
    { name: "summary", label: "Ringkasan", type: "textarea" },
    { name: "content", label: "Isi", type: "textarea" },
    { name: "image", label: "URL Gambar" },
    { name: "category", label: "Kategori" },
  ],
  vouchers: [
    { name: "code", label: "Kode" },
    { name: "type", label: "Tipe", type: "select", options: ["percent", "fixed"] },
    { name: "value", label: "Nilai", type: "number" },
    { name: "min_purchase", label: "Min Pembelian", type: "number" },
    { name: "max_discount", label: "Max Diskon", type: "number" },
    { name: "usage_limit", label: "Limit Pemakaian", type: "number" },
    { name: "valid_until", label: "Valid Hingga (ISO)" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  tests: [
    { name: "title", label: "Judul" },
    { name: "slug", label: "Slug" },
    { name: "category", label: "Kategori", type: "select", options: ["iq", "personality", "career", "interest", "development", "school_readiness", "other"] },
    { name: "target_audience", label: "Target Peserta (mis. Remaja - Dewasa)" },
    { name: "description", label: "Deskripsi singkat", type: "textarea" },
    { name: "long_description", label: "Deskripsi panjang", type: "textarea" },
    { name: "price", label: "Harga (0 = gratis)", type: "number" },
    { name: "duration_minutes", label: "Durasi (menit)", type: "number" },
    { name: "image", label: "URL Gambar" },
    { name: "scoring_type", label: "Tipe Skoring", type: "select", options: ["sum", "category"] },
    { name: "questions", label: "Daftar Pertanyaan (JSON)", type: "json", placeholder: '[{"text":"...","options":[{"label":"...","value":1}]}]' },
    { name: "result_tiers", label: "Tier Hasil (JSON)", type: "json", placeholder: '[{"min_score":0,"max_score":5,"title":"...","description":"..."}]' },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  subscriptions: [
    { name: "title", label: "Nama Plan" },
    { name: "slug", label: "Slug" },
    { name: "description", label: "Deskripsi", type: "textarea" },
    { name: "price", label: "Harga", type: "number" },
    { name: "billing_period", label: "Periode", type: "select", options: ["monthly", "yearly"] },
    { name: "features", label: "Fitur (1 per baris)", type: "lines" },
    { name: "is_popular", label: "Tandai sebagai Populer", type: "checkbox" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  banners: [
    { name: "title", label: "Judul" },
    { name: "subtitle", label: "Subtitle", type: "textarea" },
    { name: "image", label: "URL Gambar (1600x900 preferred)" },
    { name: "cta_text", label: "Teks Tombol" },
    { name: "cta_link", label: "Link Tombol (mis. /products atau https://...)" },
    { name: "sort_order", label: "Urutan", type: "number" },
    { name: "starts_at", label: "Mulai (ISO, opsional)" },
    { name: "ends_at", label: "Berakhir (ISO, opsional)" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  popups: [
    { name: "title", label: "Judul" },
    { name: "content", label: "Konten (HTML diperbolehkan)", type: "textarea" },
    { name: "image", label: "URL Gambar (opsional)" },
    { name: "cta_text", label: "Teks Tombol" },
    { name: "cta_link", label: "Link Tombol" },
    { name: "target", label: "Target", type: "select", options: ["all", "guests", "users"] },
    { name: "display_frequency", label: "Frekuensi", type: "select", options: ["once", "per_session", "always"] },
    { name: "starts_at", label: "Mulai (opsional)" },
    { name: "ends_at", label: "Berakhir (opsional)" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
  announcements: [
    { name: "text", label: "Teks pengumuman" },
    { name: "link", label: "Link (opsional)" },
    { name: "link_text", label: "Teks link (mis. 'Belanja')" },
    { name: "starts_at", label: "Mulai (opsional)" },
    { name: "ends_at", label: "Berakhir (opsional)" },
    { name: "is_active", label: "Aktif", type: "checkbox" },
  ],
};

function Dashboard() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  const cards = [
    { label: "Total Users", value: stats.users || 0, icon: Users },
    { label: "Total Produk", value: stats.products || 0, icon: Package },
    { label: "Total Layanan", value: stats.services || 0, icon: Briefcase },
    { label: "Total Event", value: stats.events || 0, icon: Calendar },
    { label: "Total Pesanan", value: stats.orders || 0, icon: ShoppingCart },
    { label: "Pesanan Dibayar", value: stats.paid_orders || 0, icon: ShoppingCart },
  ];
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-4xl text-[#1A1728] mb-2">Dashboard</h1>
      <p className="text-[#5C5870] mb-8">Ringkasan platform Lumina</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-8">
        {cards.map(c => (
          <div key={c.label} className="lumina-card p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-[#5C5870] uppercase tracking-wider">{c.label}</div>
              <c.icon className="w-4 h-4 text-[#6B4BA3]" />
            </div>
            <div className="font-serif text-4xl text-[#1A1728]">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="lumina-card p-7 bg-gradient-to-br from-[#6B4BA3] to-[#9D84D2] text-white">
        <div className="text-xs uppercase tracking-wider text-white/80 mb-2">Total Revenue</div>
        <div className="font-serif text-5xl">{formatRupiah(stats.revenue || 0)}</div>
        <div className="text-white/80 text-sm mt-2">Dari {stats.paid_orders || 0} pesanan yang sudah dibayar</div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFCFE] flex" data-testid="admin-shell">
      <aside className="w-64 bg-white border-r border-[#E2DFED] min-h-screen p-6 flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6B4BA3] to-[#9D84D2] flex items-center justify-center">
            <span className="text-white font-serif">H</span>
          </div>
          <div>
            <div className="font-serif text-lg">HearInsight</div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C5870]">Admin</div>
          </div>
        </Link>
        <nav className="flex-1 space-y-1">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${isActive ? "bg-[#E8F0FA] text-[#6B4BA3] font-medium" : "text-[#5C5870] hover:bg-[#F1EBFA]/50"}`}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(" ", "-")}`}>
              <l.icon className="w-4 h-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[#E2DFED] pt-4 space-y-2">
          <div className="px-3">
            <div className="text-xs text-[#5C5870]">Login sebagai</div>
            <div className="text-sm font-medium truncate">{user?.email}</div>
          </div>
          <Link to="/" className="flex items-center gap-2 px-3 py-2 text-sm text-[#5C5870] hover:text-[#6B4BA3]"><ArrowLeft className="w-4 h-4" /> Lihat Situs</Link>
          <button onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-2 px-3 py-2 text-sm text-[#5C5870] hover:text-[#6B4BA3] w-full text-left" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Keluar</button>
        </div>
      </aside>
      <main className="flex-1 p-10 overflow-auto">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<AdminCrud name="products" label="Produk Digital" fields={FIELDS.products} />} />
          <Route path="services" element={<AdminCrud name="services" label="Layanan" fields={FIELDS.services} />} />
          <Route path="counselors" element={<AdminCrud name="counselors" label="Counselors" fields={FIELDS.counselors} />} />
          <Route path="events" element={<AdminCrud name="events" label="Event" fields={FIELDS.events} />} />
          <Route path="testimonials" element={<AdminCrud name="testimonials" label="Testimoni" fields={FIELDS.testimonials} />} />
          <Route path="case-studies" element={<AdminCrud name="case-studies" label="Studi Kasus" fields={FIELDS["case-studies"]} />} />
          <Route path="vouchers" element={<AdminCrud name="vouchers" label="Voucher" fields={FIELDS.vouchers} />} />
          <Route path="tests" element={<AdminTests />} />
          <Route path="licenses" element={<AdminLicenses />} />
          <Route path="subscriptions" element={<AdminCrud name="subscriptions" label="Membership" fields={FIELDS.subscriptions} />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="banners" element={<AdminCrud name="banners" label="Hero Banner" fields={FIELDS.banners} />} />
          <Route path="popups" element={<AdminCrud name="popups" label="Popup Banner" fields={FIELDS.popups} />} />
          <Route path="announcements" element={<AdminCrud name="announcements" label="Announcement Bar" fields={FIELDS.announcements} />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="settings" element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
}
